import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { IntakeDocumentType } from "@/src/models/constants/document-types";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { patientSchema } from "@/src/models/patient";

export const intakeTypeDisplay: Record<IntakeDocumentType, string> = {
  [IntakeDocumentType.INTAKE]: "Intake",
  [IntakeDocumentType.INTAKE_DIALYSIS_QUESTIONAIRE]: "Dialysis Questionaire",
};

export const intakeTypeSchema = z.nativeEnum(IntakeDocumentType);

// The option catalog for the three checklists below is tenant vocabulary, supplied per group via
// blocks.yaml `options`/`description`. Consumers depend on the null/empty distinction stated here.
const CHECKLIST_PROMPT = `Return only the options the document affirmatively documents, and omit any
the document is silent about. Return an empty list when the document addresses this topic and none of
the options apply, and null when it does not address the topic at all.`;

const baseIntakeFields = {
  patient: patientSchema,
};

const extendedIntakeSchema = z.object({
  isAdw: isAdwSchema,
  authorizationNumber: z
    .string()
    .nullable()
    .describe(
      `Authorization or referral number associated with the patient's insurance or referral.

     Look for the value next to or below labels like:
     - "Authorization", "Auth #", "Auth Number", "Pre-Auth", "Prior Auth"
     - "Referral #", "Referral Number", "Ref #", "Ref Number"
     - "Certification Number", "Cert #"

     Return only the alphanumeric identifier (may include hyphens).
     Do not confuse with Policy ID, Group Number, Member ID, or Subscriber ID.`
    ),
  dateReceived: DocumentDateSchema.shape.dateReceived,
  dateOfEvent: DocumentDateSchema.shape.dateOfEvent,
  intakeNote: z.string().nullable(),
  infectionRisks: z
    .array(z.string())
    .nullable()
    .describe(`Infections the patient has that may pose a risk to others.\n\n${CHECKLIST_PROMPT}`),
  medicalAccommodations: z
    .array(z.string())
    .nullable()
    .describe(`Medical accommodations the patient needs during treatment.\n\n${CHECKLIST_PROMPT}`),
  specialAccommodations: z
    .array(z.string())
    .nullable()
    .describe(`Non-clinical accommodations the patient needs during treatment.\n\n${CHECKLIST_PROMPT}`),
});

const intakeCustomFields: Record<IntakeDocumentType, Record<string, z.ZodTypeAny>> = {
  [IntakeDocumentType.INTAKE]: extendedIntakeSchema.pick({
    isAdw: true,
    authorizationNumber: true,
    dateReceived: true,
    dateOfEvent: true,
    intakeNote: true,
  }).shape,
  [IntakeDocumentType.INTAKE_DIALYSIS_QUESTIONAIRE]: extendedIntakeSchema.pick({
    dateReceived: true,
    dateOfEvent: true,
    intakeNote: true,
    infectionRisks: true,
    medicalAccommodations: true,
    specialAccommodations: true,
  }).shape,
};

const makeIntakeSchema = (type: IntakeDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseIntakeFields,
    ...customFields,
  });

export const intakeSchemas: Record<IntakeDocumentType, ReturnType<typeof makeIntakeSchema>> = (
  Object.values(IntakeDocumentType) as IntakeDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeIntakeSchema(type, intakeCustomFields[type]);

    return acc;
  },
  {} as Record<IntakeDocumentType, ReturnType<typeof makeIntakeSchema>>
);

const intakeSchemasArray = (Object.values(IntakeDocumentType) as IntakeDocumentType[]).map(
  (type) => intakeSchemas[type]
) as [ReturnType<typeof makeIntakeSchema>, ...ReturnType<typeof makeIntakeSchema>[]];

export const anyIntakeSchema = z.discriminatedUnion("type", intakeSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Intake = ExtractedDocumentFields<IntakeDocumentType, typeof extendedIntakeSchema, typeof baseIntakeFields> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const intakeShape: Intake = {
  id: "",
  type: IntakeDocumentType.INTAKE,
  patient: null,
  authorizationNumber: null,
  dateReceived: null,
  dateOfEvent: null,
  intakeNote: null,
  infectionRisks: null,
  medicalAccommodations: null,
  specialAccommodations: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
